import React from "react";
import { Info } from "lucide-react";
import { Card, CardContent } from "src/components/ui/Card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { SessionSchedulingType } from "src/services/sessionsService";
import { SchedulingPurchasedPlan } from "src/services/adminSchedulingService";

interface BatchEntitlementLedgerCardProps {
  selectedPlan: SchedulingPurchasedPlan | undefined;
  selectedPlanCapacity: number;
  scheduledDatesCount: number;
  remainingAfterSave: number;
  schedulingType: SessionSchedulingType;
}

export const BatchEntitlementLedgerCard: React.FC<BatchEntitlementLedgerCardProps> = ({
  selectedPlan,
  selectedPlanCapacity,
  scheduledDatesCount,
  remainingAfterSave,
  schedulingType,
}) => {
  const progressPercent = selectedPlan
    ? Math.min(100, (scheduledDatesCount / Math.max(1, selectedPlanCapacity)) * 100)
    : 66.6;

  return (
    <Card className="border border-slate-200 rounded-xl shadow-xs overflow-hidden bg-white">
      <div className="bg-[#faf9fe] px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
            Batch Entitlement Ledger
          </h3>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-600 transition p-0.5 rounded-full inline-flex items-center cursor-help"
                  aria-label="Entitlement ledger details"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs p-2.5 bg-slate-900 text-slate-100 shadow-lg">
                Scheduled sessions automatically link to the student&apos;s active purchased entitlement line upon save.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-baseline justify-between">
          <div>
            <span className="text-2xl font-bold font-mono text-slate-900">
              {selectedPlanCapacity}
            </span>
            <span className="text-xs text-slate-500 font-medium ml-1">sessions available</span>
          </div>
          <span className="text-xs font-mono text-slate-500">
            {selectedPlan?.total_sessions ?? 12} Total Purchased
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="text-xs text-slate-600 space-y-1 bg-purple-50/70 p-3 rounded-lg border border-purple-200">
          <div className="flex justify-between">
            <span>Scheduling {schedulingType === "recurring" ? "series" : "session"}:</span>
            <span className="font-bold text-purple-700 font-mono">
              -{scheduledDatesCount} session{scheduledDatesCount === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex justify-between pt-1 border-t border-purple-200/80 font-semibold text-slate-800">
            <span>Remaining after save:</span>
            <span className="font-mono text-purple-900 font-bold">
              {remainingAfterSave} session{remainingAfterSave === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

